import {Injectable} from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {Snapshot} from "../models/snpashot.model";
import {ArticleDgSearchResults} from "../models/datagrid-search-results/article.dg-search-results.model";
import {Article, ArticleDescriptor} from "../models/article.model";
import {PetSpecies} from "../models/pet-species.model";

/**
 * Serwis zarządzający treścią w aplikacji Dashboard.
 * Obsługuje artykuły, sekcje FAQ, historię zmian (wersjonowanie) oraz
 * przypisywanie specyficznych treści marketingowych do kont partnerskich.
 */
@Injectable({
  providedIn: 'root'
})
export class ArticleService implements DataGridServiceInterface<Observable<ArticleDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę artykułów dla głównego widoku tabelarycznego.
   *
   * @param limit Rozmiar strony (maksymalna liczba rekordów).
   * @param offset Przesunięcie wyników.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Pole po którym sortujemy.
   * @param showDeletedObjects Flaga uwzględniająca usunięte artykuły (Archiwum).
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ArticleDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ArticleDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Article(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Filtruje artykuły na podstawie wyszukiwarki zaawansowanej z uwzględnieniem paginacji.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie wyników.
   * @param searchFieldsData Mapa zawierająca pola i frazy do wyszukania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Pole po którym sortujemy.
   * @param showDeletedObjects Flaga uwzględniająca usunięte artykuły.
   * @returns Observable emitujący wyfiltrowane wyniki tabeli.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ArticleDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/articles/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ArticleDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Article(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje istniejący artykuł w bazie danych (Update).
   *
   * @param articleEdited Obiekt artykułu ze zmodyfikowanymi danymi.
   * @returns Observable emitujący zaktualizowany obiekt artykułu z serwera.
   */
  updateArticle(articleEdited: Article): Observable<Article> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/articles', articleEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy nowy artykuł w systemie (Create).
   *
   * @param article Nowy, przygotowany obiekt artykułu.
   * @returns Observable emitujący zapisany artykuł (zawierający już nadany UUID).
   */
  addArticle(article: Article): Observable<Article> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/articles`, article).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły pojedynczego artykułu w celu załadowania go do formularza edycji.
   *
   * @param uuid Unikalny identyfikator artykułu.
   * @returns Observable emitujący pełen obiekt artykułu.
   */
  getArticleToUpdate(uuid: String): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pojedynczy wpis z bazy wiedzy (FAQ) na podstawie jego identyfikatora.
   *
   * @param uuid Unikalny identyfikator artykułu.
   * @returns Observable emitujący artykuł zdefiniowany jako FAQ.
   */
  getFAQArticle(uuid: String): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/faq/articles/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pojedynczy wpis FAQ wykorzystując przyjazny adres URL (Alias) zamiast UUID.
   *
   * @param alias Unikalny ciąg znaków przypisany do artykułu.
   * @returns Observable emitujący znaleziony artykuł FAQ.
   */
  getFAQArticleByAlias(alias: String): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/faq/articles/alias/${alias}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian (wersjonowanie) dla konkretnego artykułu.
   *
   * @param uuid Unikalny identyfikator artykułu.
   * @returns Observable emitujący tablicę migawek (Snapshots) reprezentujących poprzednie stany treści.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa artykuł z systemu na podstawie jego identyfikatora.
   *
   * @param uuid Unikalny identyfikator artykułu do usunięcia.
   * @returns Observable potwierdzający usunięcie lub rzucający błąd walidacji z API.
   */
  deleteArticle(uuid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/articles/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera globalny słownik gatunków zwierząt.
   * @returns Observable emitujący tablicę obsługiwanych gatunków.
   */
  getGlobalPetSpecies(): Observable<PetSpecies[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/species`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new PetSpecies(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zmienia status artykułu na publicznie widoczny.
   *
   * @param uuid Unikalny identyfikator artykułu.
   * @returns Observable potwierdzający zmianę statusu.
   */
  publish(uuid: string): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/publish/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zmienia status artykułu na szkic (ukrywa przed użytkownikami końcowymi).
   *
   * @param uuid Unikalny identyfikator artykułu.
   * @returns Observable potwierdzający wycofanie z publikacji.
   */
  unpublish(uuid: string): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/un-publish/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza, czy dla danego slotu partnerskiego (uuid) jest już przypisany jakiś promowany artykuł.
   */
  checkPromotedArticleForPartner(uuid: any): Observable<Article> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/promoted/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza konfigurację ekranu powitalnego (Welcome Article) dla danego klienta.
   */
  checkWelcomeArticleForPartner(vClientUUID: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/welcome/${vClientUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Przypisuje konkretny artykuł do slotu promocyjnego dla wskazanego klienta.
   */
  setPromotedArticle(vClientUUID: any, uuid: any, articleUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/partner-promoted/${vClientUUID}/${uuid}/${articleUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa artykuł ze slotu promocyjnego.
   */
  cancelArticlePromotion(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/partner-promoted-cancel/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Resetuje konfigurację ekranu powitalnego dla wskazanego partnera.
   */
  cancelWelcomeArticle(vClientUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/partner-welcome-cancel/${vClientUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Ustawia konkretny artykuł jako powitalny dla danego klienta.
   */
  setWelcomeArticle(vClientUUID: string, articleUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/partner-welcome/${vClientUUID}/${articleUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje okładkę/zdjęcie dla artykułu wewnątrz przypisanego mu slotu promocyjnego partnera.
   */
  changePromotedArticlePicture(vClientUUID: any, uuid: any, articleUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/partner-promoted-photo/${vClientUUID}/${uuid}/${articleUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<Article>(new Article(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera zbiór deskryptorów artykułów (uproszczonych obiektów) powiązanych z określoną kategorią.
   */
  getSitesbiCategoryArticleDescriptors(categoryUUID: string): Observable<ArticleDescriptor[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/articles/stsb/${categoryUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver(new ArticleDescriptor(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
